import isEmpty from "lodash-es/isEmpty";
import without from "lodash-es/without";
import { useCallback, useEffect, useMemo, useState } from "react";

import { isPastDate } from "@bokio/bank/src/utils/paymentUtils";
import RenderRequest from "@bokio/components/RenderRequest/RenderRequest";
import { SignAndPayContent } from "@bokio/components/SignAndPay/SignAndPayModal/SignAndPayContent/SignAndPayContent";
import {
	clearPaymentSigningProgressInfoFromLocalStorage,
	readPaymentSigningProgressInfoFromLocalStorage,
	savePaymentSigningProgressInfoToLocalStorage,
	useBankIdPolling,
} from "@bokio/components/SveaBankIdAuthModal/sveaPollingUtils";
import { SveaBankIdFragment } from "@bokio/components/SveaBankIdFragment/SveaBankIdFragment";
import { useSveaBankIdState } from "@bokio/components/SveaBankIdFragment/useSveaBankIdState";
import { AppMessageType, useAppContext } from "@bokio/contexts/AppContext/AppContext";
import { usePaymentContext } from "@bokio/contexts/PaymentContext/PaymentContext";
import { LoadingContent } from "@bokio/designsystem/components/Loading";
import { SG } from "@bokio/designsystem/components/SpacingGroup/SpacingGroup";
import { BankLangFactory } from "@bokio/lang";
import { useCompanyInfo } from "@bokio/mobile-web-shared/core/contexts/CompanyInfoContext/CompanyInfoContext";
import * as m from "@bokio/mobile-web-shared/core/model/model";
import { useActiveBankConnection } from "@bokio/mobile-web-shared/hooks/useActiveBankConnection/useActiveBankConnection";
import { useLazyApi } from "@bokio/mobile-web-shared/hooks/useApi/useApi";
import * as proxy from "@bokio/mobile-web-shared/services/api/proxy";
import { formatNumberCurrency } from "@bokio/shared/utils/format";

import type { DelegateInformation } from "@bokio/mobile-web-shared/areas/bank/hooks/useNewSveaAuthentication";
import type { RequestState } from "@bokio/mobile-web-shared/services/api/requestState";
import type { Dispatch, ReactNode, SetStateAction } from "react";

type PaymentValidationRulesDto = m.Bokio.Bank.Contract.Dtos.PaymentValidationRulesDto;

type FailedPaymentGroups = m.Bokio.Bank.Contract.Dtos.FailedPaymentGroup;
type PaymentGroupForAuthorization = m.Bokio.Bank.Contract.Dtos.PaymentGroupForAuthorization;
type PaymentGroupForListDto = m.Bokio.Bank.Contract.Dtos.PaymentGroupForListDto;
const enum ErrorTypes {
	Cancelled = 1,
	Rejected = 2,
	UnHandledPollStatus = 3,
	AuthError = 4,
	RowErrors = 5,
	UserCanNotSign = 6,
}
import BankIdCollectionStatus = m.Bokio.Common.Contract.BankId.BankIdCollectionStatus;

type SignAndPayError =
	| { type: ErrorTypes.AuthError; message: string }
	| { type: Exclude<ErrorTypes, ErrorTypes.AuthError> };

const getSelectedPaymentsTotal = (
	paymentGroups: PaymentGroupForListDto[],
	selectedPaymentGroupIds: string[],
): { total: number; multipleCurrencies: boolean } => {
	const selectedPayments = paymentGroups.filter(x => selectedPaymentGroupIds.includes(x.Id)).flatMap(g => g.Payments);
	const currencies = new Set(selectedPayments.map(p => p.Currency));

	const selectedApproximateTotal = selectedPayments.reduce((sum, payment) => sum + payment.ApproximateDomesticSum, 0);

	return {
		total: selectedApproximateTotal,
		multipleCurrencies: currencies.size > 1,
	};
};

const getPaymentGroupForAuth = (
	defaultPaymentDate: m.Day,
	paymentGroup: m.Bokio.Bank.Contract.Dtos.PaymentGroupForListDto,
): PaymentGroupForAuthorization => {
	// TODO: Better way to do this for groups??
	// TODO move isPastDate to core
	return {
		Id: paymentGroup.Id,
		Date: isPastDate(paymentGroup.Payments[0]) ? defaultPaymentDate : paymentGroup.Date,
	};
};

export type RestrictedAccessState = "fullAccess" | "noAccess" | "partialAccess";

export interface useSignAndPayReturn {
	paymentListRequest: RequestState<m.Envelope<m.Bokio.Bank.Contract.Dtos.PaymentsForListDto, m.SimpleError>>;
	refreshPaymentListAndSummary: () => void;
	availableBalance: string;
	failedPaymentGroups: Record<string, FailedPaymentGroups>;
	error: string | undefined;
	stopPollingAndCancelSigning: () => void;
	isPolling: boolean;
	authorizeRequest: RequestState<
		m.Envelope<m.Bokio.Bank.Contract.Dtos.SendAndAuthorizePaymentsResultDto, m.Bokio.Bank.Contract.Dtos.BankError>
	>;
	togglePaymentGroupSelection: (id: string, selected: boolean) => void;
	availablePaymentGroupIds: string[];
	availablePaymentGroups: PaymentGroupForListDto[];
	permittedPaymentGroupIds: Set<string>;
	restrictedAccessState: RestrictedAccessState;
	totalSum: number;
	multipleCurrencies: boolean;
	selectedPaymentGroupIds: string[];
	setSelectedPaymentGroupIds: Dispatch<SetStateAction<string[]>>;
	isAnyRequestLoading: boolean;
	disableSign: boolean;
	paymentValidationRules: PaymentValidationRulesDto;
	executeAuthorize: () => Promise<void>;
	renderSignAndPayContent: (onClose: () => void) => ReactNode;
}

interface useSignAndPayProps {
	paymentValidationRules: PaymentValidationRulesDto;
	initiallySelectedPaymentGroupIds?: string[];
	persistToast?: boolean | undefined;
	onSubmit: () => void;
	onAuthorizationFailed?: () => void;
	/**
	 * PSU access info from the Svea delegate authentication gate (SveaDelegates feature).
	 * When provided and access is "restricted", only payments from accounts the user has
	 * `approvePayment` permission for will be shown.
	 */
	currentPsuInfo?: DelegateInformation["psus"][number] | null;
	/**
	 * Used when you want to create a payment and directly sign in
	 * Used in BBA migration.
	 */
	createPaymentAndexecuteAuthorizeEndpoint?: (
		org: string,
		paymentGroups: PaymentGroupForAuthorization[],
	) => Promise<
		m.Envelope<m.Bokio.Bank.Contract.Dtos.SendAndAuthorizePaymentsResultDto, m.Bokio.Bank.Contract.Dtos.BankError>
	>;
}

export const useSignAndPay = ({
	paymentValidationRules,
	initiallySelectedPaymentGroupIds,
	persistToast,
	onSubmit,
	onAuthorizationFailed,
	currentPsuInfo,
	createPaymentAndexecuteAuthorizeEndpoint,
}: useSignAndPayProps): useSignAndPayReturn => {
	const bankLang = BankLangFactory();
	const { companyInfo } = useCompanyInfo();
	const { activeBusinessAccountExternalSystem, activeBusinessAccount } = useActiveBankConnection();
	const { currentUserPermission, refreshSignablePaymentCount } = usePaymentContext();

	const [selectedPaymentGroupIds, setSelectedPaymentGroupIds] = useState<string[]>(
		initiallySelectedPaymentGroupIds ?? [],
	);
	// This is a ref to make sure the value is changed before the polling is started
	const [failedPaymentGroups, setFailedPaymentGroups] = useState<Record<string, FailedPaymentGroups>>({});
	const [error, setError] = useState<SignAndPayError>();
	const app = useAppContext();
	const addMessage = app.addMessage;

	const { bankIdState, resetBankIdState, setBankIdState } = useSveaBankIdState();

	const getError = (error: SignAndPayError | undefined) => {
		if (error === undefined) {
			return;
		}
		let message = bankLang.SignAndPayModalError;
		switch (error.type) {
			case ErrorTypes.AuthError:
				message = error.message;
				break;
			case ErrorTypes.Cancelled:
				message = bankLang.BankIdPolling_CancelledByUser;
				break;
			case ErrorTypes.Rejected:
				message = bankLang.BankIdPolling_Rejected;
				break;
			case ErrorTypes.UnHandledPollStatus:
				message = bankLang.BankIdPolling_UnHandledPollStatus;
				break;
			case ErrorTypes.RowErrors:
				message = bankLang.SignAndPayModalError_RowErrors;
				break;
			case ErrorTypes.UserCanNotSign:
				message = bankLang.SignAndPayModal_NoRights;
				break;
		}
		return message;
	};

	const [refreshPaymentCentralSummaryRequest, paymentCentralSummaryRequest] = useLazyApi(
		proxy.Bank.PaymentController.GetPaymentCentralSummary.Get,
	);

	const domesticCurrency = companyInfo.CountryMetadata.Currency;
	const availableAmount = paymentCentralSummaryRequest.data?.Data?.AvailableAmount;
	const availableBalance =
		availableAmount !== undefined ? formatNumberCurrency(availableAmount, domesticCurrency) : "-";

	const [refreshPaymentList, paymentListRequest] = useLazyApi(proxy.Bank.PaymentController.ListSignablePayments.Get, {
		onSuccess: data => {
			const allGroupIds = data.PaymentGroups.map(x => x.Id);

			// Compute permitted IDs inline (memo hasn't recalculated yet)
			let permittedIds: Set<string>;
			if (currentPsuInfo === null) {
				permittedIds = new Set<string>();
			} else if (currentPsuInfo?.accessLevel.id === "restricted") {
				const permittedAccountIds = new Set(
					currentPsuInfo.accessLevel.contexts
						.filter(ctx => ctx.accessLevel === "approvePayment")
						.map(ctx => ctx.bankAccountId),
				);
				permittedIds = new Set(
					data.PaymentGroups.filter(pg => permittedAccountIds.has(String(pg.FromBankAccount))).map(pg => pg.Id),
				);
			} else {
				permittedIds = new Set(allGroupIds);
			}

			const hasInprogressPaymentSigning = readPaymentSigningProgressInfoFromLocalStorage();
			if (hasInprogressPaymentSigning && hasInprogressPaymentSigning.paymentGroupIds) {
				setSelectedPaymentGroupIds(hasInprogressPaymentSigning.paymentGroupIds);
				return;
			}

			if (selectedPaymentGroupIds.length === 0) {
				setSelectedPaymentGroupIds(allGroupIds.filter(id => permittedIds.has(id)));
				return;
			}

			const selectedButUnavailableIds = without(selectedPaymentGroupIds, ...allGroupIds);
			const nonPermittedIds = selectedPaymentGroupIds.filter(id => !permittedIds.has(id));
			// Remove unavailable and non-permitted ids from selection
			setSelectedPaymentGroupIds(prev => without(prev, ...selectedButUnavailableIds, ...nonPermittedIds));
		},
	});

	if (!activeBusinessAccountExternalSystem) {
		throw new Error("At least one business account must be active");
	}

	const refreshPaymentListAndSummary = async () => {
		await refreshPaymentCentralSummaryRequest(companyInfo.Id);
		await refreshPaymentList(companyInfo.Id, []);
	};

	const [cancelConfirmPayment] = useLazyApi(proxy.Bank.PaymentController.CancelConfirmPayment.Post);

	const { startPolling, stopPolling, isPolling } = useBankIdPolling(
		"useSignAndPay",
		proxy.Bank.PaymentController.ConfirmPayment.Post,
		{
			isPending: data => data.CollectionStatus == BankIdCollectionStatus.Pending,
			effect: async data => {
				switch (data?.CollectionStatus) {
					case BankIdCollectionStatus.Complete:
						clearPaymentSigningProgressInfoFromLocalStorage();
						resetBankIdState();
						await refreshPaymentListAndSummary();
						await refreshSignablePaymentCount();
						addMessage({
							type: AppMessageType.InfoMessage,
							message: bankLang.SignAndPayModalSuccess,
							persist: persistToast ?? false,
						});

						onSubmit();
						return;
					case BankIdCollectionStatus.Pending:
						setBankIdState({ bankIdQrCode: data.QrCode, collectionStatus: data.CollectionStatus });
						return;
					case BankIdCollectionStatus.Failed:
					case BankIdCollectionStatus.Invalid:
						clearPaymentSigningProgressInfoFromLocalStorage();
						setError({ type: ErrorTypes.UnHandledPollStatus });
						return;
				}
			},
			onStopped: context => {
				if (context?.error) {
					setError({ type: ErrorTypes.AuthError, message: context.error.Message });
				}

				if (context?.error || context?.apiError) {
					clearPaymentSigningProgressInfoFromLocalStorage();
					resetBankIdState();
					return;
				}
			},
		},
	);

	const [executeAuthorizeRequest, authorizeRequest] = useLazyApi(
		createPaymentAndexecuteAuthorizeEndpoint || proxy.Bank.PaymentController.SendAndAuthorizePayments.Post,
		{
			onSuccess: async data => {
				if (createPaymentAndexecuteAuthorizeEndpoint) {
					// Refresh the payments because the creation of payment is done inside executeAuthorizeEndpoint
					await refreshPaymentListAndSummary();
					await refreshSignablePaymentCount();
				}
				setFailedPaymentGroups(data.FailedPaymentGroups);

				if (!isEmpty(data.FailedPaymentGroups)) {
					onAuthorizationFailed?.();
					setError({ type: ErrorTypes.RowErrors });
					clearPaymentSigningProgressInfoFromLocalStorage();
					return;
				}

				const savedProgress = readPaymentSigningProgressInfoFromLocalStorage();
				if (savedProgress) {
					savePaymentSigningProgressInfoToLocalStorage({
						...savedProgress,
						isPolling: true,
						SuccessfulPaymentIds: data.SuccessfulPaymentIds,
						signingToken: data.AutostartToken,
					});
				}

				setBankIdState(
					{
						autoStartToken: data?.AutostartToken,
						bankIdQrCode: data?.QrCode,
						hintCode: m.Bokio.Common.Contract.BankId.BankIdHintCode.OutstandingTransaction,
					},
					{ lockQrCode: true },
				);
				startPolling([companyInfo.Id, data.AutostartToken, data.SuccessfulPaymentIds]);
			},
			onError: data => setError({ type: ErrorTypes.AuthError, message: data.Message }),
		},
	);

	const availablePaymentGroups = useMemo(() => {
		return paymentListRequest.data?.Data?.PaymentGroups ?? [];
	}, [paymentListRequest.data]);

	const permittedPaymentGroupIds = useMemo(() => {
		// Authenticated but no PSU found for this company â†’ no permission to sign
		if (currentPsuInfo === null) {
			return new Set<string>();
		}
		if (currentPsuInfo?.accessLevel.id === "restricted") {
			const permittedAccountIds = new Set(
				currentPsuInfo.accessLevel.contexts
					.filter(ctx => ctx.accessLevel === "approvePayment")
					.map(ctx => ctx.bankAccountId),
			);
			return new Set(
				availablePaymentGroups.filter(pg => permittedAccountIds.has(String(pg.FromBankAccount))).map(pg => pg.Id),
			);
		}
		// Non-restricted users can sign all payments
		return new Set(availablePaymentGroups.map(pg => pg.Id));
	}, [availablePaymentGroups, currentPsuInfo]);

	const restrictedAccessState: RestrictedAccessState = useMemo(() => {
		if (availablePaymentGroups.length === 0) {
			return "fullAccess";
		}
		if (permittedPaymentGroupIds.size === 0) {
			return "noAccess";
		}
		if (permittedPaymentGroupIds.size < availablePaymentGroups.length) {
			return "partialAccess";
		}
		return "fullAccess";
	}, [availablePaymentGroups, permittedPaymentGroupIds]);

	// Deselect non-permitted payments when permissions change (e.g. after BankID authentication)
	useEffect(() => {
		if (availablePaymentGroups.length === 0) {
			return;
		}
		setSelectedPaymentGroupIds(prev => {
			const filtered = prev.filter(id => permittedPaymentGroupIds.has(id));
			return filtered.length === prev.length ? prev : filtered;
		});
	}, [availablePaymentGroups, permittedPaymentGroupIds]);

	const availablePaymentGroupIds = availablePaymentGroups.map(x => x.Id);

	const fromAccountsInfo = paymentListRequest.data?.Data?.FromAccountsInfo ?? {};

	const executeAuthorize = useCallback(async () => {
		const isDelegateAuthenticated = currentPsuInfo !== undefined;
		if (!isDelegateAuthenticated && !currentUserPermission.CanSignPayment) {
			setError({ type: ErrorTypes.UserCanNotSign });
			clearPaymentSigningProgressInfoFromLocalStorage();
			return;
		}

		setError(undefined);
		await executeAuthorizeRequest(
			companyInfo.Id,
			availablePaymentGroups
				.filter(ap => selectedPaymentGroupIds.includes(ap.Id))
				.map(p => getPaymentGroupForAuth(paymentValidationRules.NextPossiblePaymentDateIncludingToday, p)),
		);
	}, [
		availablePaymentGroups,
		companyInfo.Id,
		selectedPaymentGroupIds,
		currentPsuInfo,
		currentUserPermission.CanSignPayment,
		executeAuthorizeRequest,
		paymentValidationRules.NextPossiblePaymentDateIncludingToday,
	]);

	const togglePaymentGroupSelection = (id: string, selected: boolean) => {
		if (selected && !permittedPaymentGroupIds.has(id)) {
			return;
		}
		if (selected) {
			setSelectedPaymentGroupIds(selectedPaymentGroupIds.concat(id));
			return;
		}

		setSelectedPaymentGroupIds(selectedPaymentGroupIds.filter(x => x !== id));
	};

	const { total, multipleCurrencies } = getSelectedPaymentsTotal(availablePaymentGroups, selectedPaymentGroupIds);
	const isAnyRequestLoading = paymentListRequest.isLoading || authorizeRequest.isLoading || isPolling;

	const disableSign =
		isAnyRequestLoading ||
		selectedPaymentGroupIds.length < 1 ||
		selectedPaymentGroupIds.some(sp => failedPaymentGroups[sp] !== undefined) ||
		activeBusinessAccount?.KycInformation?.RequiredAction === m.Bokio.Common.Helpers.SveaKycAction.BlockPayments;

	const stopPollingAndCancelSigning = () => {
		if (bankIdState.autoStartToken && !error) {
			cancelConfirmPayment(companyInfo.Id, activeBusinessAccountExternalSystem, bankIdState.autoStartToken);
		}

		stopPolling();
		resetBankIdState();
	};

	const renderSignAndPayContent = (onClose: () => void) => (
		<RenderRequest
			request={paymentListRequest}
			whenLoading={
				<SG padding="16">
					<LoadingContent />
				</SG>
			}
		>
			{() => (
				<SignAndPayContent
					selectedPaymentGroupIds={selectedPaymentGroupIds}
					isPolling={isPolling}
					onGlobalCheckClick={checked =>
						setSelectedPaymentGroupIds(
							checked ? availablePaymentGroupIds.filter(id => permittedPaymentGroupIds.has(id)) : [],
						)
					}
					availablePaymentGroups={availablePaymentGroups}
					permittedPaymentGroupIds={permittedPaymentGroupIds}
					fromAccountsInfo={fromAccountsInfo}
					failedPaymentGroups={failedPaymentGroups}
					disabled={isAnyRequestLoading}
					error={getError(error)}
					togglePaymentGroupSelection={togglePaymentGroupSelection}
					onClose={onClose}
					paymentValidationRules={paymentValidationRules}
				>
					<SG padding="16">
						<SveaBankIdFragment bankIdProps={bankIdState} />
					</SG>
				</SignAndPayContent>
			)}
		</RenderRequest>
	);

	return {
		paymentListRequest,
		refreshPaymentListAndSummary,
		availableBalance: availableBalance,
		failedPaymentGroups,
		error: getError(error),
		stopPollingAndCancelSigning,
		isPolling,
		authorizeRequest,
		togglePaymentGroupSelection,
		availablePaymentGroupIds,
		availablePaymentGroups,
		permittedPaymentGroupIds,
		restrictedAccessState,
		totalSum: total,
		multipleCurrencies,
		selectedPaymentGroupIds,
		setSelectedPaymentGroupIds,
		isAnyRequestLoading,
		disableSign,
		paymentValidationRules,
		executeAuthorize: async () => {
			const activeSveaPaymentSigningToken = readPaymentSigningProgressInfoFromLocalStorage();
			if (activeSveaPaymentSigningToken && !activeSveaPaymentSigningToken.isPolling) {
				await executeAuthorize();
			} else {
				if (!isPolling) {
					stopPolling();
				}
				if (
					activeSveaPaymentSigningToken &&
					activeSveaPaymentSigningToken.signingToken &&
					activeSveaPaymentSigningToken.SuccessfulPaymentIds
				) {
					setBankIdState(
						{
							autoStartToken: activeSveaPaymentSigningToken.signingToken,
							bankIdQrCode: undefined,
							hintCode: m.Bokio.Common.Contract.BankId.BankIdHintCode.OutstandingTransaction,
						},
						{ lockQrCode: false },
					);
					startPolling([
						companyInfo.Id,
						activeSveaPaymentSigningToken.signingToken,
						activeSveaPaymentSigningToken.SuccessfulPaymentIds,
					]);
				}
			}
		},
		renderSignAndPayContent,
	};
};
